import { Field, Form, FormikProps } from 'formik';
import { MultiValue } from 'react-select';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useCurrentEnvironment } from '@/react/hooks/useCurrentEnvironment';
import { Registry } from '@/react/portainer/registries/types/registry';
import { Authorized, useAuthorizations } from '@/react/hooks/useUser';
import { useEnvironmentRegistries } from '@/react/portainer/environments/queries/useEnvironmentRegistries';
import { getRegistryPolicy } from '@/react/portainer/registries/utils/getRegistryPolicy';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { Input } from '@@/form-components/Input';
import { FormActions } from '@@/form-components/FormActions';
import { SystemBadge } from '@@/Badge/SystemBadge';

import { Annotations } from '../../../annotations';
import { IngressClassDatatable } from '../../../cluster/ingressClass/IngressClassDatatable';
import { useIngressControllerClassMapQuery } from '../../../cluster/ingressClass/useIngressControllerClassMap';
import { NamespaceFormValues } from '../../types';
import { NamespaceSummary } from '../NamespaceSummary';
import { useIsSystemNamespace } from '../../queries/useIsSystemNamespace';
import { isDefaultNamespace } from '../../isDefaultNamespace';

import { LoadBalancerFormSection } from './LoadBalancerFormSection';
import { StorageQuotaFormSection } from './StorageQuotaFormSection/StorageQuotaFormSection';
import { ResourceQuotaFormSection } from './ResourceQuotaFormSection';
import { RegistriesFormSection } from './RegistriesFormSection';
import { ResourceQuotaFormValues } from './ResourceQuotaFormSection/types';
import { LoadBalancerQuotaFormValues } from './LoadBalancerFormSection/types';
import { StorageQuotaFormValues } from './StorageQuotaFormSection/types';
import { ToggleSystemNamespaceButton } from './ToggleSystemNamespaceButton';

const namespaceWriteAuth = 'K8sResourcePoolDetailsW';

export function NamespaceInnerForm({
  errors,
  isValid,
  dirty,
  setFieldValue,
  values,
  initialValues,
  isEdit,
  isUpdating = false,
}: FormikProps<NamespaceFormValues> & {
  isEdit?: boolean;
  isUpdating?: boolean;
}) {
  const { authorized: hasNamespaceWriteAuth } = useAuthorizations(
    namespaceWriteAuth,
    undefined,
    true
  );
  const isSystemNamespace = useIsSystemNamespace(values.name, isEdit === true);
  const isEditingDisabled =
    !hasNamespaceWriteAuth ||
    isDefaultNamespace(values.name) ||
    isSystemNamespace;

  const environmentId = useEnvironmentId();
  const environmentQuery = useCurrentEnvironment();
  const registriesQuery = useEnvironmentRegistries(environmentId, {
    hideDefault: true,
  });
  const registryPolicy = getRegistryPolicy(environmentQuery.data);
  const ingressClassesQuery = useIngressControllerClassMapQuery({
    environmentId,
    namespace: values.name,
    allowedOnly: true,
  });

  const useLoadBalancer =
    environmentQuery.data?.Kubernetes.Configuration.UseLoadBalancer;
  const enableResourceOverCommit =
    environmentQuery.data?.Kubernetes.Configuration.EnableResourceOverCommit;
  const enableIngressControllersPerNamespace =
    environmentQuery.data?.Kubernetes.Configuration
      .IngressAvailabilityPerNamespace;
  const storageClasses =
    environmentQuery.data?.Kubernetes.Configuration.StorageClasses ?? [];

  return (
    <Form className="form-horizontal">
      <FormControl
        inputId="namespace"
        label="Name"
        required={!isEdit}
        errors={errors.name}
      >
        {isEdit ? (
          <div className="mt-2 flex gap-2">
            {values.name}
            {isSystemNamespace && <SystemBadge />}
          </div>
        ) : (
          <Field
            as={Input}
            id="namespace"
            name="name"
            disabled={isEdit}
            placeholder="e.g. my-namespace"
            data-cy="k8sNamespaceCreate-namespaceNameInput"
          />
        )}
      </FormControl>
      <Annotations
        placeholder={['e.g. app.kubernetes.io/name', 'examplename']}
        initialAnnotations={initialValues.annotations}
        errors={errors.annotations}
        handleUpdateAnnotations={(annotations) =>
          setFieldValue('annotations', annotations)
        }
        hideForm={isEditingDisabled}
      />
      {(values.resourceQuota.enabled || !isEditingDisabled) && (
        <ResourceQuotaFormSection
          isEdit={isEdit}
          enableResourceOverCommit={enableResourceOverCommit}
          values={values.resourceQuota}
          onChange={(resourceQuota: ResourceQuotaFormValues) =>
            setFieldValue('resourceQuota', resourceQuota)
          }
          errors={errors.resourceQuota}
          namespaceName={values.name}
          isEditingDisabled={isEditingDisabled}
        />
      )}
      {useLoadBalancer && (
        <LoadBalancerFormSection
          namespace={values.name}
          values={values.loadBalancerQuota}
          onChange={(loadBalancerQuota: LoadBalancerQuotaFormValues) =>
            setFieldValue('loadBalancerQuota', loadBalancerQuota)
          }
          errors={errors.loadBalancerQuota}
          isEdit={isEdit}
        />
      )}
      {enableIngressControllersPerNamespace && (
        <Authorized authorizations={[namespaceWriteAuth]}>
          <FormSection title="Networking">
            <IngressClassDatatable
              onChange={(classes) => setFieldValue('ingressClasses', classes)}
              values={values.ingressClasses}
              description="Enable the ingress controllers that users can select when publishing applications in this namespace."
              noIngressControllerLabel="No ingress controllers available in the cluster. Go to the cluster setup view to configure and allow the use of ingress controllers in the cluster."
              view="namespace"
              isLoading={ingressClassesQuery.isLoading}
              initialValues={initialValues.ingressClasses}
            />
          </FormSection>
        </Authorized>
      )}
      {storageClasses.length > 0 && !isEditingDisabled && (
        <StorageQuotaFormSection
          environmentId={environmentId}
          namespace={values.name}
          storageClasses={storageClasses}
          values={values.storageQuota}
          onChange={(storageQuota: StorageQuotaFormValues[]) =>
            setFieldValue('storageQuota', storageQuota)
          }
          errors={errors.storageQuota}
          isEdit={isEdit}
        />
      )}
      <RegistriesFormSection
        values={values.registries}
        onChange={(registries: MultiValue<Registry>) =>
          setFieldValue('registries', registries)
        }
        errors={errors.registries}
        isEditingDisabled={isEditingDisabled}
        registryOptions={registriesQuery.data}
        isLoadingRegistries={registriesQuery.isLoading}
        registryPolicy={registryPolicy}
      />
      <Authorized authorizations={[namespaceWriteAuth]}>
        <NamespaceSummary
          initialValues={initialValues}
          values={values}
          isValid={isValid}
        />
        <FormActions
          submitLabel={isEdit ? 'Update namespace' : 'Create namespace'}
          loadingText={isEdit ? 'Updating namespace' : 'Creating namespace'}
          isLoading={isUpdating}
          isValid={isValid && dirty}
          data-cy="k8sNamespaceCreate-submitButton"
        >
          {isEdit && (
            <ToggleSystemNamespaceButton
              isSystemNamespace={isSystemNamespace}
              isEdit={isEdit}
              environmentId={environmentId}
              namespaceName={values.name}
            />
          )}
        </FormActions>
      </Authorized>
    </Form>
  );
}
